import React from "react";
import * as Mui from "@mui/material";
import { makeStyles } from "tss-react/mui";
import * as Icons from "react-feather";

interface Props {
	open: boolean;
	title: string;
	confirmLabel: string;
	body: React.ReactNode;
	onCancel: () => void;
	onConfirm: () => void;
}

// Buttons align to the shared "Unsaved changes" modal (MAX-3831 review): both actions are a fixed
// 36px tall with a 2px border on the secondary action, and an 8px gap between them.
const useStyles = makeStyles()((theme) => ({
	// #5F6368 — same close-icon color as the shared Unsaved changes modal.
	closeButton: {
		color: theme.palette.custom.textSecondary,
	},
	actions: {
		gap: 8,
		padding: "8px 24px 24px",
	},
	// Outlined secondary — 2px border, fixed 36px height (border-box keeps the border inside).
	secondaryButton: {
		height: 36,
		boxSizing: "border-box",
		borderWidth: 2,
		borderRadius: 8,
		"&:hover": { borderWidth: 2 },
	},
	// Filled primary — transparent 2px border + fixed 36px height so both actions match exactly.
	primaryButton: {
		height: 36,
		boxSizing: "border-box",
		border: "2px solid transparent",
		borderRadius: 8,
	},
}));

// Shown when enabling a high-impact routing rule (First Touch Attribution / Featured Listings),
// so agents understand the override behavior before the setting is applied.
export const ConfirmationModal: React.FC<Props> = ({ open, title, confirmLabel, body, onCancel, onConfirm }) => {
	const { classes } = useStyles();
	return (
		<Mui.Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
			<Mui.DialogTitle>
				<Mui.Box display="flex" alignItems="center" justifyContent="space-between">
					<Mui.Typography variant="h6" style={{ fontWeight: "bold", fontSize: 24 }}>{title}</Mui.Typography>
					<Mui.IconButton size="small" className={classes.closeButton} onClick={onCancel}>
						<Icons.X size={24} />
					</Mui.IconButton>
				</Mui.Box>
			</Mui.DialogTitle>
			<Mui.Divider style={{ marginLeft: 24, marginRight: 24 }} />
			<Mui.DialogContent style={{ padding: "16px 24px 16px" }}>
				{body}
			</Mui.DialogContent>
			<Mui.DialogActions className={classes.actions} disableSpacing>
				<Mui.Button className={classes.secondaryButton} variant="outlined" color="primary" onClick={onCancel}>
					Cancel
				</Mui.Button>
				<Mui.Button className={classes.primaryButton} variant="contained" color="primary" onClick={onConfirm}>
					{confirmLabel}
				</Mui.Button>
			</Mui.DialogActions>
		</Mui.Dialog>
	);
};
